import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Settings, Send, Newspaper, Clock, ExternalLink, Eye, RefreshCcw, Cloud, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth, getLastSignedInEmail } from "@/contexts/AuthContext";
import RoosterLogo from "@/components/RoosterLogo";
import EmailFrame from "@/components/EmailFrame";
import ForecastLoader from "@/components/ForecastLoader";
import NewsLoader from "@/components/NewsLoader";
import AccuracyScorecard from "@/components/AccuracyScorecard";
import StocksPanel from "@/components/StocksPanel";
import { runJob } from "@/lib/pollJob";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ localStorage instant-load cache ============
// We stash the last-successful weather + news HTML in localStorage,
// keyed by user email. On next dashboard mount we paint that cached
// content SYNCHRONOUSLY before any network call — the user sees their
// forecast in <100ms even on a cold SW + cold backend. We still fetch
// fresh in the background and swap when ready.
//
// TTL of 12h guards against users seeing a stale forecast for days if
// they stop opening the app. Stale-but-recent is better than "Building
// your forecast..." every single login.
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
const CACHE_KEY = (email) => `wv:dashboard-cache:${email || "anon"}`;

function readCache(email) {
  try {
    const raw = localStorage.getItem(CACHE_KEY(email));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.saved_at || Date.now() - data.saved_at > CACHE_TTL_MS) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeCache(email, patch) {
  try {
    const existing = readCache(email) || {};
    const next = { ...existing, ...patch, saved_at: Date.now() };
    localStorage.setItem(CACHE_KEY(email), JSON.stringify(next));
  } catch {
    /* quota / disabled — fine to ignore */
  }
}

/**
 * Format an ISO UTC timestamp as "Last refreshed at 7:02 AM" in the
 * user's local timezone. Returns "" on null/invalid input so the UI can
 * just conditionally render the whole string.
 *
 * Rationale: the dashboard heavily caches previews (6h window) so the
 * content a user sees at 10am may have been built at 7am when the
 * scheduler ran. Showing the timestamp confirms the caching is working
 * FOR them (this matches what was emailed) and nudges them to use
 * Refresh if they want fresh numbers.
 */
function formatRefreshedAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * AppDashboard — the primary signed-in home for Weathervane.
 *
 * Pulls /api/me which returns identity + manage_token + the user's full
 * subscription lists. Renders a clean two-section layout (Weather, News)
 * with quick actions on every row. Deep-edit affordances (add/remove
 * locations, change delivery time, edit topics) live on the existing
 * /manage/{token} page — we link there with a clear "Edit subscriptions"
 * button so we don't duplicate that UI here.
 */
export default function AppDashboard() {
  const { user, logout, authConfig } = useAuth();
  const navigate = useNavigate();

  // Seed all cached state from localStorage SYNCHRONOUSLY so the first
  // paint already shows yesterday's (or this morning's) forecast instead
  // of "Building your forecast..." — huge perceived-latency win.
  //
  // IMPORTANT: `user` comes from AuthContext which fetches /api/auth/me
  // asynchronously, so `user?.email` is UNDEFINED on first render. We
  // use the persisted `wv:last_email` as a fallback so the cache key is
  // stable across reloads and the synchronous initialCache lookup
  // actually hits. Without this, every page load missed cache and
  // people saw the loader for the full 8-15s cold path.
  const cacheEmail = user?.email || getLastSignedInEmail();
  const initialCache = readCache(cacheEmail);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // 'weather' | 'news' | null
  // The active tab is persisted in the URL (?tab=weather|news) so a
  // refresh — including a hard reload from the News tab — returns the
  // user to where they were instead of bouncing back to Weather.
  // useSearchParams gives a reactive snapshot of ?tab=… and a setter
  // that updates the URL without triggering a full re-mount.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (() => {
    const t = searchParams.get("tab");
    if (t === "news") return "news";
    if (t === "stocks") return "stocks";
    return "weather";
  })();
  const setTab = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next === "weather") sp.delete("tab");
    else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  // Weather forecast HTML (inline view) — primed from cache.
  const [weatherHtml, setWeatherHtml] = useState(initialCache?.weather_html || "");
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherErr, setWeatherErr] = useState("");
  const [weatherRefreshing, setWeatherRefreshing] = useState(false);
  const [weatherGeneratedAt, setWeatherGeneratedAt] = useState(initialCache?.weather_generated_at || null);

  // News digest HTML (inline view) — primed from cache, keyed by news-sub id.
  const [newsViewerSubId, setNewsViewerSubId] = useState(initialCache?.news_viewer_sub_id || null);
  const [newsHtmlBySubId, setNewsHtmlBySubId] = useState(initialCache?.news_html_by_sub_id || {});
  const [newsLoadingId, setNewsLoadingId] = useState(null);
  const [newsErr, setNewsErr] = useState("");
  const [newsGeneratedAt, setNewsGeneratedAt] = useState(initialCache?.news_generated_at || {});

  const load = async () => {
    // If we already painted cached content, skip the big "loading..." overlay
    // — we'll just silently refresh in the background. Only show the blocking
    // loader on a truly cold first-time mount.
    if (!weatherHtml) setLoading(true);
    try {
      const r = await axios.get(`${API}/me`, authConfig());
      // If we just came back from an OAuth signup that started on the
      // landing page, drain the pending-locations stash now that we have
      // a manage_token. We do this BEFORE setData so the UI shows the
      // freshly-added locations on first paint.
      const drained = await drainPendingSignupLocations(r.data, authConfig());
      if (drained > 0) {
        toast.success(
          drained === 1
            ? "Welcome to Weathervane. Your first location is set."
            : `Welcome to Weathervane. ${drained} locations are set.`,
        );
        // Re-fetch to pick up the new subs.
        const r2 = await axios.get(`${API}/me`, authConfig());
        setData(r2.data);
      } else {
        setData(r.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const sendNowWeather = async () => {
    if (!data?.manage_token) return;
    setSending("weather");
    try {
      await axios.post(`${API}/manage/${data.manage_token}/send-now`);
      toast.success("Forecast sent. Check your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(null);
    }
  };

  const sendNowNews = async (newsId) => {
    if (!data?.manage_token) return;
    setSending(`news-${newsId}`);
    try {
      await runJob(`${API}/manage/${data.manage_token}/news/${newsId}/send-now/start`, undefined, { timeoutMs: 240000 });
      toast.success("News digest sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(null);
    }
  };

  // ============ Inline weather-preview loader ============
  // Fetches one rendered email per active subscription, post-processes the
  // Prepare an email-HTML document for in-browser iframe rendering on
  // the Dashboard. The dashboard already shows the Weathervane logo,
  // wordmark, and "Hi, {first_name}" in its own React header — so the
  // email's matching hero + greeting + footer would duplicate that and
  // create a "page within a page" feel. We hide them here via CSS.
  // Real emails sent to inboxes are unaffected — the chrome only
  // disappears in this iframe context.
  //
  // Also drops the email-shell white card + outer F9 padding so the
  // forecast content blends into the dashboard background instead of
  // sitting inside a visible bordered box.
  //
  // Font-size scaling is handled DYNAMICALLY inside EmailFrame.js via
  // `transform: scale`-aware inverse-scaling injection.
  const _prepareDoc = (h) => {
    if (!h) return "";
    let out = h;
    out = out.replace(/<head([^>]*)>/i, '<head$1><base target="_top">');
    const overrides =
      '<style>' +
      '@media (prefers-color-scheme: dark){' +
      '  .wv-logo-img{filter:none !important;}' +
      '  .wv-logo-dark{display:none !important;}' +
      '  .wv-logo-light{display:block !important;}' +
      '  body{background:#F9F9F9 !important;}' +
      '}' +
      // Hide the email-only chrome on the dashboard so the forecast
      // doesn't read as a duplicate "Weathervane / Good morning" card
      // sitting INSIDE the React dashboard that already shows those.
      '.wv-email-hero{display:none !important;}' +
      '.wv-email-greeting{display:none !important;}' +
      '.wv-email-footer{display:none !important;}' +
      // Banter is the editorial one-liner. Per product decision, it
      // belongs only in the email — the dashboard is the user's own
      // workspace and doesn't need an opening joke. Hide it here so the
      // forecast content sits flush against the dashboard chrome.
      '.wv-email-banter{display:none !important;}' +
      // With the greeting hidden, the enclosing td (.wv-email-intro) would
      // still contribute its 24px top padding AND the banter below still
      // keeps its 14px top margin — giving ~38px of dead space above the
      // bolded banter line. Collapse both so the banter sits tight under
      // the dashboard's "Last refreshed at" metadata.
      '.wv-email-intro{padding-top:0 !important; padding-bottom:4px !important;}' +
      '.wv-email-intro p[style*="italic"]{margin-top:0 !important;}' +
      // Drop the white card border + outer padding so content sits flush
      // with the dashboard. CRITICAL: keep the body background EXPLICITLY
      // #F9F9F9 (same as React dashboard) — making it `transparent` lets
      // the iframe fall through to the OS canvas color, which is BLACK
      // in macOS/Chrome dark mode and made text unreadable. Matching the
      // dashboard tone keeps the page seamless in both light and dark.
      '.wv-email-shell{border:none !important; background:#F9F9F9 !important;}' +
      '.wv-email-outer{padding:0 !important; background:#F9F9F9 !important;}' +
      'body{background:#F9F9F9 !important;}' +
      '</style>';
    out = out.replace(/<\/head>/i, `${overrides}</head>`);
    return out;
  };

  const loadWeather = async (forceRefresh = false) => {
    if (!data?.manage_token) return;
    const token = data.manage_token;
    const activeSubs = (data.subscriptions || []).filter((s) => s.active);
    if (!activeSubs.length) {
      setWeatherErr("Add a location to see your forecast.");
      return;
    }
    // ALWAYS show the full ForecastLoader on any reload event — silent
    // background "swap" was confusing because the Refresh button looked
    // like it did nothing while the request flew. The user explicitly
    // asked for the building animation on every reload trigger. We
    // also blank out the existing iframe so the loader is what's on
    // screen during the wait.
    if (forceRefresh) {
      setWeatherHtml("");
      setWeatherGeneratedAt(null);
    }
    setWeatherLoading(true);
    setWeatherRefreshing(forceRefresh);
    setWeatherErr("");
    try {
      if (forceRefresh) {
        await axios.post(
          `${API}/manage/${token}/subscriptions/preview/refresh`,
        ).catch(() => {});
      }
      // ONE combined email for ALL locations — single header, single
      // footer, location banners inline. Mirrors the actual daily email
      // the scheduler sends, so dashboard preview matches inbox.
      // 60s timeout — weather builds are faster than news (no Perplexity
      // round-trip), but a network blip should not trap the loader.
      const r = await axios.post(
        `${API}/manage/${token}/subscriptions/preview`,
        undefined,
        { timeout: 60000 },
      );
      const preparedHtml = _prepareDoc(r.data?.html || "");
      setWeatherHtml(preparedHtml);
      setWeatherGeneratedAt(r.data?.generated_at || null);
      // Stash for instant-load on next visit. Use cacheEmail (same
      // fallback-aware key that read the initial cache) so the next
      // mount's readCache actually finds what we wrote here.
      writeCache(user?.email || cacheEmail, {
        weather_html: preparedHtml,
        weather_generated_at: r.data?.generated_at || null,
      });
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      setWeatherErr(
        isTimeout
          ? "Weather forecast is taking unusually long to load. Tap Refresh to try again."
          : (e?.response?.data?.detail || "Couldn't load your forecast right now."),
      );
    } finally {
      setWeatherLoading(false);
      setWeatherRefreshing(false);
    }
  };

  // Auto-load the weather preview as soon as the dashboard data arrives.
  // We're showing the Weather tab by default, so users see their forecast
  // immediately on landing — no second click required.
  //
  // If we already have cached HTML from a previous session, this call
  // runs as a SILENT background refresh (no blocking loader). Users see
  // yesterday's forecast instantly, then the refreshed version swaps in.
  useEffect(() => {
    if (data?.manage_token && !weatherLoading && !weatherErr) {
      loadWeather(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.manage_token]);

  // Pre-load news previews for ALL feeds in parallel as soon as the
  // dashboard data arrives, so switching to the News tab is instant.
  // We DON'T re-fetch on tab toggle — results are cached in
  // newsHtmlBySubId. Refresh button (or a new feed being added) is the
  // only re-fetch trigger, keeping Perplexity token use bounded.
  //
  // SAFETY (iter 40): every request gets a 90-second timeout AND an
  // AbortController. Without these, a stalled fetch would trap the
  // NewsLoader on screen forever (the loader hides when EITHER cached
  // data lands OR an error is set — neither happens for a hung request).
  // 90s is generous enough for a fully-cold Perplexity + RSS-fallback
  // cycle (~45-60s observed) with headroom; anything longer is a
  // genuine network failure and we'd rather surface a retry button
  // than spin forever.
  useEffect(() => {
    if (!data?.manage_token) return;
    const feeds = (data.news_subscriptions || []).filter((n) => n.active);
    if (!feeds.length) return;
    const controllers = feeds.map(() => new AbortController());
    feeds.forEach((n, idx) => {
      if (newsHtmlBySubId[n.id]) return; // already cached
      const ctrl = controllers[idx];
      runJob(
        `${API}/manage/${data.manage_token}/news/${n.id}/preview/start`,
        undefined,
        { timeoutMs: 90000, signal: ctrl.signal },
      )
        .then((payload) => {
          const html = _prepareDoc(payload?.html || "");
          const cacheKey = user?.email || cacheEmail;
          setNewsHtmlBySubId((prev) => {
            if (prev[n.id]) return prev;
            const next = { ...prev, [n.id]: html };
            // Write-through cache so next visit paints instantly.
            writeCache(cacheKey, { news_html_by_sub_id: next });
            return next;
          });
          if (payload?.generated_at) {
            setNewsGeneratedAt((prev) => {
              const next = { ...prev, [n.id]: payload.generated_at };
              writeCache(cacheKey, { news_generated_at: next });
              return next;
            });
          }
          // Auto-select the first feed so the News tab opens with content
          // visible — no extra "View" click required.
          setNewsViewerSubId((prev) => {
            const pick = prev || n.id;
            writeCache(cacheKey, { news_viewer_sub_id: pick });
            return pick;
          });
        })
        .catch((e) => {
          // Ignore deliberate aborts (component unmount, navigate away).
          if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
            return;
          }
          // Surface a friendly error so the loader exits and the user
          // gets a retry path. Without this, a stalled / timed-out
          // fetch would leave the loader spinning forever (the bug
          // user reported on iter 39).
          console.warn("news preload failed:", e?.message);
          setNewsErr(
            e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")
              ? "News digest is taking unusually long to load. Tap Refresh to try again."
              : (e?.response?.data?.detail || "Couldn't load your news digest right now. Tap Refresh to try again."),
          );
        });
    });
    // Cancel any in-flight requests on unmount (or when the dashboard
    // re-loads with a different token). This avoids state-on-unmounted
    // warnings and lets the user click Refresh without two requests
    // racing.
    return () => {
      controllers.forEach((c) => {
        try { c.abort(); } catch { /* noop */ }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.manage_token]);

  // ============ Inline news-preview loader ============
  // Used as a force-reload only — initial fetch happens in the pre-load
  // effect on dashboard mount, so the News tab opens with content visible
  // without any user action.
  const loadNews = async (newsId, forceRefresh = false) => {
    if (!data?.manage_token) return;
    setNewsLoadingId(newsId);
    setNewsErr("");
    // Scroll the loading animation into view so the user doesn't think
    // the Refresh button did nothing. Without this, an existing-cache
    // refresh would render the loader in place but the user might be
    // scrolled down to the Refresh button and never see it.
    // Use rAF so the loader has mounted before we scroll.
    try {
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-testid="dashboard-news-loading"]')
                || document.querySelector('[data-testid="dashboard-news-frame"]');
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    } catch { /* noop */ }
    try {
      // Force-refresh: evict the 6-hour server-side HTML cache FIRST so
      // the subsequent preview call rebuilds from scratch. Without this
      // the "Reload" button served stale HTML for up to 6 hours even
      // after the user deliberately asked for fresh — defeating the
      // whole point of a reload button.
      if (forceRefresh) {
        try {
          await axios.post(
            `${API}/manage/${data.manage_token}/news/${newsId}/preview/refresh`
          );
        } catch {
          // Non-fatal — if the eviction endpoint fails we still fall
          // through to the preview call below.
        }
      }
      const payload = await runJob(
        `${API}/manage/${data.manage_token}/news/${newsId}/preview/start`,
        undefined,
        // 180s (was 90s). Cold Perplexity + RSS-fallback + URL
        // validation on a slow-Perplexity day can legitimately take
        // 100-140s. Polling (instead of one long request) keeps each
        // round-trip under the production edge proxy's ~30s cap.
        { timeoutMs: 180000 },
      );
      const html = _prepareDoc(payload?.html || "");
      const cacheKey = user?.email || cacheEmail;
      setNewsHtmlBySubId((prev) => {
        const next = { ...prev, [newsId]: html };
        writeCache(cacheKey, { news_html_by_sub_id: next });
        return next;
      });
      if (payload?.generated_at) {
        setNewsGeneratedAt((prev) => {
          const next = { ...prev, [newsId]: payload.generated_at };
          writeCache(cacheKey, { news_generated_at: next });
          return next;
        });
      }
      setNewsViewerSubId(newsId);
    } catch (e) {
      // Differentiate timeout from generic failures so the message is
      // actionable. Without this users see a vague "couldn't build"
      // even when the real cause is a 90s stall — they need to know
      // a retry might just work.
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      setNewsErr(
        isTimeout
          ? "News digest is taking unusually long to load. Tap Refresh to try again."
          : (e?.response?.data?.detail || "Couldn't build the news digest right now."),
      );
    } finally {
      setNewsLoadingId(null);
    }
  };

  const subs = (data?.subscriptions || []).filter((s) => s.active);
  const newsSubs = (data?.news_subscriptions || []).filter((n) => n.active);
  const token = data?.manage_token;

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10 bg-white sticky top-0 z-30 backdrop-blur-sm bg-white/95">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <RoosterLogo size={41} />
            <div className="min-w-0">
              <div className="font-display text-xl sm:text-2xl font-black tracking-tighter truncate leading-none">Weathervane</div>
              <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-neutral-500 font-bold mt-1 truncate">Weather &amp; News <span className="mx-1">/</span> <span className="italic">BYO Coffee</span></div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Link to="/admin" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 border border-black bg-white hover:bg-[#FFD600] text-[10px] uppercase tracking-[0.18em] font-bold">
                Admin
              </Link>
            )}
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-black bg-white hover:bg-[#FFD600] text-[10px] uppercase tracking-[0.18em] font-bold"
            >
              <LogOut className="w-3 h-3" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        {loading && !weatherHtml && (
          <div className="flex flex-col items-center justify-center py-20 text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin mb-3" />
            <div className="ticker-label">Loading your stuff…</div>
          </div>
        )}

        {(!loading || weatherHtml) && (
          <>
            <div className="mb-10" data-testid="dashboard-welcome">
              <div className="ticker-label mb-2">Welcome back</div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                Hi, {user?.first_name || "friend"}.
              </h1>
              <p className="mt-2 text-neutral-700 max-w-xl">
                Your weather and news, exactly how you like them. Tap a row to dig in.
              </p>
            </div>

            {/* Storm-alert / push opt-in is iOS/Android-only — hidden on
                web because almost no one has browser push enabled and
                it just adds noise. The native apps will surface this
                via APNs/FCM. */}

            {/* ============== Tabs ============== */}
            <div className="border-b-2 border-black mb-6 flex gap-0 -mx-1 sm:mx-0" role="tablist" data-testid="dashboard-tabs">
              <button
                role="tab"
                aria-selected={tab === "weather"}
                data-testid="tab-weather"
                onClick={() => setTab("weather")}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 sm:px-7 py-3 text-[11px] uppercase tracking-[0.18em] font-bold border-2 border-black border-b-0 -mb-[2px] transition-colors ${
                  tab === "weather"
                    ? "bg-black text-[#FFD600]"
                    : "bg-white text-black hover:bg-[#FFFBE6]"
                }`}
              >
                <Cloud className="w-3.5 h-3.5" /> Weather
                <span className="text-[9px] opacity-70 ml-0.5">{subs.length}</span>
              </button>
              <button
                role="tab"
                aria-selected={tab === "news"}
                data-testid="tab-news"
                onClick={() => setTab("news")}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 sm:px-7 py-3 text-[11px] uppercase tracking-[0.18em] font-bold border-2 border-black border-b-0 border-l-0 -mb-[2px] transition-colors ${
                  tab === "news"
                    ? "bg-black text-[#FFD600]"
                    : "bg-white text-black hover:bg-[#FFFBE6]"
                }`}
              >
                <Newspaper className="w-3.5 h-3.5" /> News
                <span className="text-[9px] opacity-70 ml-0.5">{newsSubs.length}</span>
              </button>
              <button
                role="tab"
                aria-selected={tab === "stocks"}
                data-testid="tab-stocks"
                onClick={() => setTab("stocks")}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 sm:px-7 py-3 text-[11px] uppercase tracking-[0.18em] font-bold border-2 border-black border-b-0 border-l-0 -mb-[2px] transition-colors ${
                  tab === "stocks"
                    ? "bg-black text-[#FFD600]"
                    : "bg-white text-black hover:bg-[#FFFBE6]"
                }`}
              >
                <TrendingUp className="w-3.5 h-3.5" /> Top 10 Stocks
              </button>
            </div>

            {/* ============== Weather tab ============== */}
            {tab === "weather" && (
              <div className="mb-12" data-testid="dashboard-weather-section">
                {subs.length === 0 ? (
                  <EmptyCard
                    title="No locations yet."
                    body="Add the places you care about — home, work, mom&rsquo;s house, your favorite trail."
                    cta="Add a location"
                    to={`/manage/${token}?add=1`}
                    testid="dashboard-weather-empty"
                  />
                ) : (
                  <>
                    <div className="mb-3">
                      <div className="text-sm text-neutral-700">
                        {subs.length === 1 ? (
                          <>Showing today&rsquo;s forecast for <strong>{subs[0].location_label}</strong>.</>
                        ) : (
                          <>Showing today&rsquo;s forecast for {subs.length} locations.</>
                        )}
                        {weatherGeneratedAt && (
                          <span
                            className="block text-[11px] text-neutral-500 mt-1"
                            data-testid="dashboard-weather-refreshed-at"
                          >
                            Last refreshed at {formatRefreshedAt(weatherGeneratedAt)}
                          </span>
                        )}
                      </div>
                    </div>

                    {weatherErr && (
                      <div className="border-2 border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm mb-4" data-testid="dashboard-weather-error">
                        {weatherErr}
                      </div>
                    )}
                    {(weatherLoading || (!weatherHtml && !weatherErr)) && (
                      <div data-testid="dashboard-weather-loading">
                        <ForecastLoader label="Building your forecast" />
                      </div>
                    )}
                    {weatherHtml && !weatherLoading && (
                      <div className="max-w-[640px] mx-auto" data-testid="dashboard-weather-frame">
                        <EmailFrame html={weatherHtml} wrapperId="dashboard-weather-iframe-wrap" />
                      </div>
                    )}

                    {/* Action row at the bottom of the forecast — users
                        scroll past the content first, then naturally see
                        the buttons in the order they'll need them
                        (refresh, email, edit). Putting them at the top
                        before any content felt premature and busy. */}
                    {weatherHtml && !weatherLoading && (
                      <div className="flex gap-2 flex-wrap mt-5 justify-center">
                        <button
                          onClick={() => loadWeather(true)}
                          disabled={weatherRefreshing || weatherLoading}
                          data-testid="dashboard-weather-refresh"
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-black bg-white hover:bg-[#FFD600] text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-50"
                        >
                          {weatherRefreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                          Refresh
                        </button>
                        <button
                          onClick={sendNowWeather}
                          disabled={sending === "weather"}
                          data-testid="dashboard-weather-sendnow"
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border border-black text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-50"
                        >
                          {sending === "weather" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          Email me now
                        </button>
                        <Link
                          to={`/manage/${token}`}
                          data-testid="dashboard-weather-edit"
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-black bg-white hover:bg-[#FFD600] text-[10px] uppercase tracking-[0.18em] font-bold"
                        >
                          <Settings className="w-3 h-3" /> Edit locations
                        </Link>
                      </div>
                    )}
                    {/* Per-location Accuracy Scorecards — shown after the
                        forecast iframe so users can scroll into the learning
                        data for each spot they track. Folded by default so
                        they don't crowd the main experience. */}
                    {subs.map((s) => (
                      <AccuracyScorecard
                        key={s.id}
                        lat={s.lat}
                        lon={s.lon}
                        label={s.location_label}
                      />
                    ))}
                    {/* The per-location "hourly" quick-link strip used
                        to live here, but it duplicated address info
                        already visible in the forecast itself. The
                        Hourly link inside each day's banner now serves
                        the same purpose without redundancy. */}
                  </>
                )}
              </div>
            )}

            {/* ============== News tab ============== */}
            {tab === "news" && (
              <div className="mb-12" data-testid="dashboard-news-section">
                {newsSubs.length === 0 ? (
                  <EmptyCard
                    title="No news feed yet."
                    body="Tell us what you actually care about and we&rsquo;ll send 6 stories a day. No doomscroll."
                    cta="Set up a feed"
                    to={`/manage/${token}`}
                    testid="dashboard-news-empty"
                  />
                ) : (() => {
                  // Decide which feed is visible. Default to the first one
                  // if nothing's been picked yet.
                  const activeId = newsViewerSubId || newsSubs[0]?.id;
                  const activeFeed = newsSubs.find((n) => n.id === activeId);
                  const isCached = activeId && !!newsHtmlBySubId[activeId];
                  const isLoadingThis = newsLoadingId === activeId;
                  return (
                    <>
                      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                        <div className="text-sm text-neutral-700">
                          {newsSubs.length === 1
                            ? "Your daily news digest, rendered live."
                            : `${newsSubs.length} feeds — pick one to read inline below.`}
                        </div>
                        <Link
                          to={`/manage/${token}`}
                          data-testid="dashboard-news-edit"
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-black bg-white hover:bg-[#FFD600] text-[10px] uppercase tracking-[0.18em] font-bold"
                        >
                          <Settings className="w-3 h-3" /> Edit feeds
                        </Link>
                      </div>

                      {/* Feed switcher — only rendered when the user has
                          multiple feeds. Each pill shows a short label
                          (first ~30 chars of the prompt). Tapping a pill
                          swaps which iframe is shown without re-fetching
                          (everything is pre-loaded on dashboard mount). */}
                      {newsSubs.length > 1 && (
                        <div
                          className="flex flex-wrap gap-2 mb-5"
                          data-testid="dashboard-news-switcher"
                        >
                          {newsSubs.map((n) => {
                            const isActive = n.id === activeId;
                            const shortLabel = (n.refined_prompt || "Feed").slice(0, 32) +
                              ((n.refined_prompt || "").length > 32 ? "…" : "");
                            return (
                              <button
                                key={n.id}
                                onClick={() =>
                                  newsHtmlBySubId[n.id]
                                    ? setNewsViewerSubId(n.id)
                                    : loadNews(n.id)
                                }
                                disabled={newsLoadingId === n.id}
                                data-testid={`dashboard-news-switch-${n.id}`}
                                title={n.refined_prompt}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold border-2 border-black ${
                                  isActive
                                    ? "bg-black text-[#FFD600]"
                                    : "bg-white text-black hover:bg-[#FFD600]"
                                } disabled:opacity-50`}
                              >
                                {newsLoadingId === n.id && (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                )}
                                {shortLabel}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {newsErr && (
                        <div className="border-2 border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm mb-4" data-testid="dashboard-news-error">
                          {newsErr}
                        </div>
                      )}

                      {/* Loading state. Renders when:
                          (a) nothing is cached for the active feed — i.e.
                              first-ever load, triggered by the pre-load
                              effect on dashboard mount; OR
                          (b) the user explicitly tapped "Refresh news"
                              (`isLoadingThis`). Without (b) the cached
                              iframe would stay visible while a silent
                              refresh ran in the background, making the
                              Refresh button look like it "did nothing"
                              until the swap happened — a real user
                              complaint from May 2026.
                          News-specific loader so the "6 weather models"
                          stages don't show while we're actually
                          scanning newsrooms. */}
                      {(!isCached || isLoadingThis) && !newsErr && (
                        <div data-testid="dashboard-news-loading">
                          <NewsLoader
                            label={isLoadingThis && isCached ? "Refreshing your news digest" : "Building your news digest"}
                          />
                        </div>
                      )}

                      {isCached && !isLoadingThis && activeFeed && (
                        <>
                          {/* The email iframe — its own header (logo +
                              "Weathervane News" + banter) opens the
                              page. We removed the duplicated bold prompt
                              card for parity with the email. */}
                          <div
                            className="max-w-[640px] mx-auto"
                            data-testid="dashboard-news-frame"
                          >
                            <EmailFrame
                              html={newsHtmlBySubId[activeId]}
                              wrapperId="dashboard-news-iframe-wrap"
                            />
                          </div>

                          {/* Meta line — sits just below the iframe
                              (right after the last story visually) so
                              users can confirm freshness without
                              scrolling back up. */}
                          <div
                            className="text-xs text-neutral-500 flex items-center justify-center gap-2 mt-4 flex-wrap"
                            data-testid={`dashboard-news-meta-${activeId}`}
                          >
                            <Clock className="w-3 h-3" />
                            daily at {activeFeed.delivery_time}
                            {newsGeneratedAt[activeId] && (
                              <>
                                <span>&middot;</span>
                                <span data-testid={`dashboard-news-refreshed-at-${activeId}`}>
                                  last refreshed {formatRefreshedAt(newsGeneratedAt[activeId])}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Action buttons at the very bottom. Refresh
                              fetches fresh content for THIS feed; Send
                              to inbox emails the same content. The
                              Refresh button is the PRIMARY action — it's
                              styled bigger and more prominent because
                              users were missing it before. */}
                          <div className="flex gap-3 flex-wrap mt-6 justify-center">
                            <button
                              onClick={() => loadNews(activeId, true)}
                              disabled={isLoadingThis}
                              data-testid={`dashboard-news-reload-${activeId}`}
                              className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#FFD600] hover:bg-black hover:text-[#FFD600] text-black border-2 border-black text-[11px] uppercase tracking-[0.18em] font-bold disabled:opacity-50 transition-colors"
                            >
                              {isLoadingThis ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                              Refresh news
                            </button>
                            <button
                              onClick={() => sendNowNews(activeId)}
                              disabled={sending === `news-${activeId}`}
                              data-testid={`dashboard-news-sendnow-${activeId}`}
                              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white hover:bg-black hover:text-[#FFD600] text-black border-2 border-black text-[11px] uppercase tracking-[0.18em] font-bold disabled:opacity-50 transition-colors"
                            >
                              {sending === `news-${activeId}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              Send to inbox
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* ============== Top-10 Stocks tab ============== */}
            {tab === "stocks" && (
              <div className="mb-12" data-testid="dashboard-stocks-section">
                <StocksPanel
                  token={token}
                  initiallySubscribed={data?.stocks_active}
                />
              </div>
            )}

            {/* Account info footer */}
            <div className="border-t border-black/10 pt-6 text-xs text-neutral-500 flex items-center justify-between gap-3 flex-wrap" data-testid="dashboard-account-info">
              <div>
                Signed in as <span className="text-neutral-800 font-medium">{user?.email}</span>
                {user?.google_sub && <span className="ml-2 text-[10px] uppercase tracking-wider bg-neutral-100 px-2 py-0.5 rounded">via Google</span>}
                {user?.apple_sub && <span className="ml-2 text-[10px] uppercase tracking-wider bg-neutral-100 px-2 py-0.5 rounded">via Apple</span>}
              </div>
              <Link to={`/manage/${token}`} className="underline hover:text-black inline-flex items-center gap-1">
                Manage subscriptions <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function EmptyCard({ title, body, cta, to, testid }) {
  return (
    <div className="border-2 border-dashed border-black/30 bg-white p-7 text-center" data-testid={testid}>
      <div className="font-display text-xl font-black tracking-tighter mb-2">{title}</div>
      <p className="text-sm text-neutral-700 mb-5 max-w-md mx-auto">{body}</p>
      <Link to={to} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border-2 border-black text-[10px] uppercase tracking-[0.18em] font-bold">
        {cta}
      </Link>
    </div>
  );
}

const PENDING_KEY = "wv:pending-signup";

/** Consume any locations the user filled in on the landing page before
 *  going through OAuth. Posts each one to /api/manage/{token}/subscriptions
 *  using the manage_token from /api/me. Returns the number of locations
 *  successfully added. Always clears the stash on completion (success or
 *  failure) so we never replay the same signup twice. */
async function drainPendingSignupLocations(meData, cfg) {
  let stash;
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return 0;
    stash = JSON.parse(raw);
  } catch {
    try { localStorage.removeItem(PENDING_KEY); } catch {}
    return 0;
  }
  // TTL guard: don't replay a stash older than its expires_at.
  if (!stash || !Array.isArray(stash.locations) || (stash.expires_at && Date.now() > stash.expires_at)) {
    try { localStorage.removeItem(PENDING_KEY); } catch {}
    return 0;
  }
  // Email mismatch guard: if the OAuth user doesn't match what was typed
  // on the landing page, drop the stash without applying — the locations
  // belonged to a different intended account.
  if (stash.email && meData?.user?.email && stash.email.toLowerCase() !== meData.user.email.toLowerCase()) {
    try { localStorage.removeItem(PENDING_KEY); } catch {}
    return 0;
  }
  // Skip if user already has overlapping subs (re-OAuth on same browser).
  const existingKeys = new Set(
    (meData?.subscriptions || []).map((s) => `${(s.lat || 0).toFixed(3)}|${(s.lon || 0).toFixed(3)}|${s.delivery_time}`),
  );
  const token = meData?.manage_token;
  if (!token) {
    try { localStorage.removeItem(PENDING_KEY); } catch {}
    return 0;
  }
  let added = 0;
  for (const loc of stash.locations) {
    const key = `${(loc.lat || 0).toFixed(3)}|${(loc.lon || 0).toFixed(3)}|${loc.delivery_time}`;
    if (existingKeys.has(key)) continue;
    try {
      await axios.post(`${API}/manage/${token}/subscriptions`, loc, cfg);
      added += 1;
    } catch (e) {
      console.warn("drainPendingSignupLocations: failed to add", loc, e);
    }
  }
  try { localStorage.removeItem(PENDING_KEY); } catch {}
  return added;
}
